import { createContext, useContext } from 'react';
import type { ILoginRequest, ILoginResponse } from './services/login.service';

/**
 * Auth context type: auth state, isUserInfoLoaded, and auth actions
 */
export interface AuthContextType {
  auth: ILoginResponse | null;
  updateAuth: (auth: ILoginResponse | null) => void;
  isLoggedIn: boolean;
  /** True after getMe() has completed on reload/login; used to avoid redirecting to dashboard before userInfo is ready */
  isUserInfoLoaded: boolean;
  login: (data: ILoginRequest) => Promise<void>;
  logout: () => void;
  setAuth: (auth: ILoginResponse | null) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

/**
 * Hook to access auth context
 *
 * @returns Auth context with auth, updateAuth, isLoggedIn, isUserInfoLoaded, login, logout, setAuth
 * @remarks isUserInfoLoaded is true after getMe() has completed (reload/login); used by PermissionGuard to avoid redirecting before userInfo is ready
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('認証情報を取得できませんでした。もう一度お試しください。');
  }
  return context;
};
